import React, { useEffect, useState } from 'react';
import { Building2, Loader2, Pencil, Plus } from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { Distribuidora } from '../../../types';
import DistribuidoraFormModal from './DistribuidoraFormModal';

const DistribuidorasView: React.FC = () => {
  const [distribuidoras, setDistribuidoras] = useState<Distribuidora[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Distribuidora | null>(null);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('distribuidora_config').select('*').order('nome');
      if (error) throw error;
      setDistribuidoras((data || []) as Distribuidora[]);
    } catch (error) {
      console.error('Error loading distribuidoras', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
      <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white font-poppins flex items-center gap-2">
            <Building2 className="text-primary" size={20} /> Distribuidoras
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Cadastro canônico usado pelas UGs — e-mail de protocolo, dia de leitura padrão e webhook próprio.
          </p>
        </div>
        <button
          onClick={() => {
            setEditing(null);
            setShowForm(true);
          }}
          className="px-4 py-2 rounded-lg bg-primary text-white font-medium hover:opacity-90 flex items-center gap-2"
        >
          <Plus size={16} /> Nova Distribuidora
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin text-gray-400" size={28} />
        </div>
      ) : distribuidoras.length === 0 ? (
        <div className="p-16 text-center">
          <Building2 className="mx-auto text-gray-300 dark:text-gray-600 mb-3" size={40} />
          <p className="text-gray-500 dark:text-gray-400">Nenhuma distribuidora cadastrada ainda.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase border-b border-gray-200 dark:border-gray-700">
                <th className="py-2 px-6">Nome</th>
                <th className="py-2 px-6">E-mail de Protocolo</th>
                <th className="py-2 px-6">Dia de Leitura</th>
                <th className="py-2 px-6">Webhook Próprio</th>
                <th className="py-2 px-6">Ativa</th>
                <th className="py-2 px-6 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {distribuidoras.map(d => (
                <tr key={d.id} className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-3 px-6 font-medium text-gray-900 dark:text-gray-100">{d.nome}</td>
                  <td className="py-3 px-6 text-gray-600 dark:text-gray-300">{d.email_protocolo || '—'}</td>
                  <td className="py-3 px-6 text-gray-600 dark:text-gray-300">
                    {d.dia_leitura_padrao ? `Dia ${d.dia_leitura_padrao}` : '—'}
                  </td>
                  <td className="py-3 px-6">
                    {d.webhook_protocolo_url ? (
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400">
                        Configurado
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="py-3 px-6 text-gray-600 dark:text-gray-300">{d.ativo ? 'Sim' : 'Não'}</td>
                  <td className="py-3 px-6 text-right">
                    <button
                      onClick={() => {
                        setEditing(d);
                        setShowForm(true);
                      }}
                      className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                    >
                      <Pencil size={14} /> Editar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <DistribuidoraFormModal
          distribuidora={editing}
          onClose={() => {
            setShowForm(false);
            setEditing(null);
          }}
          onSaved={() => {
            setShowForm(false);
            setEditing(null);
            carregar();
          }}
        />
      )}
    </div>
  );
};

export default DistribuidorasView;
